import { ChannelKind, Status } from '@api/generated/models';
import { BadgeText, HeadlessButton, Icon, Text } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import type { AccessFlow } from '@services/flexible_component/mod';
import styled, { css } from 'styled-components';

type Variants = 'slim' | 'chunky';

function getActionTextAlignSelf($hasBadge: boolean, variant: Variants): string {
	if ($hasBadge) {
		return 'stretch';
	}
	if (variant === 'chunky') {
		return 'flex-start';
	}
	return 'unset';
}

interface ActionCardProps {
	title: string;
	subTitle?: string;
	onClick?: () => void;
	icon?: JSX.Element;
	variant?: Variants;
	isStatic?: boolean;
	hasBorder?: boolean;
	hideArrow?: boolean;
	disabled?: boolean;
	buttonType?: ChannelKind | null;
	accessFlow?: AccessFlow;
}

const elements = {
	container: styled(HeadlessButton)`
		display: flex;
		justify-content: flex-start;
		align-items: center;
		overflow: hidden;
		position: relative;
		width: 100%;
		border-radius: 12px;
		padding: 8px 0;
		border: 1px solid ${tokens.color.border.muted};
		box-shadow: ${tokens.elevation.skim};
		transition: box-shadow 150ms ease-in;
		color: ${tokens.color.content.default};
		height: 100%;
		background: #fff;

		&:hover {
			transition: box-shadow 50ms;
			box-shadow: ${tokens.elevation.lifted};
		}

		&:active {
			box-shadow: none;
		}

		&:disabled,
		&[data-disabled='true'] {
			box-shadow: none;
			opacity: 0.6;
			cursor: not-allowed;
		}

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			background-color: ${primitiveColorTokens.primary[100]};
			z-index: -1;
			opacity: 0.4;
		}

		&[data-focus='true'] {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 2px;
		}
	`,
	iconContainer: styled.div<{ variant: Variants }>`
		display: flex;
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		width: ${tokens.size.x7};
		color: ${tokens.color.content.mutedBrand};
		background-color: ${tokens.color.bg.surfaceBrand};
		justify-content: center;

		${({ variant }) => {
			if (variant === 'chunky') {
				return css`
					padding: ${tokens.size.x1_5} ${tokens.size.x1_5} ${tokens.size.x9} ${tokens.size.x1_5};
				`;
			}

			return css`
				align-items: center;
				padding: ${tokens.size.x2_5} ${tokens.size.x1_5};
			`;
		}};
	`,
	arrowContainer: styled.div<{ variant: Variants }>`
		color: ${tokens.color.content.muted};
		height: 100%;
		display: flex;
		align-items: flex-end;
		padding: ${tokens.spacing.x1_5};
		flex-shrink: 0;
		align-self: ${({ variant }) => (variant === 'chunky' ? 'flex-end' : 'center')};
		padding: ${({ variant }) =>
			variant === 'chunky' ? `${tokens.size.x9} ${tokens.size.x1_5} ${tokens.size.x1_5} 0` : `${tokens.size.x1_5} `};
	`,
	actionTextContainer: styled.div<{ $hasBadge: boolean; variant: Variants }>`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1};
		flex-grow: 1;
		align-items: start;
		text-align: left;
		padding: ${tokens.spacing.x1_5} 0 ${tokens.spacing.x1_5} ${tokens.spacing.x2};
		align-self: ${({ $hasBadge, variant }) => getActionTextAlignSelf($hasBadge, variant)};

		&[data-has-icon='true'] {
			padding-left: calc(${tokens.size.x8} + ${tokens.spacing.x1});
		}
	`,
	badgeTextContainer: styled.div`
		margin-top: auto;
		padding-top: 10px;
	`,
};

export function ActionCard({
	title,
	subTitle,
	icon,
	onClick,
	hideArrow,
	isStatic = false,
	variant = 'slim',
	disabled = false,
	buttonType,
	accessFlow,
	...rest
}: ActionCardProps): JSX.Element {
	function handleOnClick(): void {
		if (disabled) {
			return;
		}

		onClick?.();
	}

	const hasTriageBadge =
		buttonType === ChannelKind.RedRobin &&
		accessFlow?.triage.status !== Status.Inactive &&
		accessFlow?.triage.status !== Status.Unavailable;
	const hasMessagingBadge =
		buttonType === ChannelKind.Messaging &&
		accessFlow?.messaging.status !== Status.Inactive &&
		accessFlow?.messaging.status !== Status.Unavailable;

	const hasBadge = hasMessagingBadge || hasTriageBadge;

	const renderBadge = () => {
		if (hasMessagingBadge) {
			return (
				<elements.badgeTextContainer>
					<BadgeText size='medium' variant={accessFlow?.messaging.isOpen ? 'success' : 'info'}>
						{accessFlow?.messaging.text ?? ''}
					</BadgeText>
				</elements.badgeTextContainer>
			);
		}
		if (hasTriageBadge) {
			return (
				<elements.badgeTextContainer>
					<BadgeText size='medium' variant={accessFlow?.triage.isOpen ? 'success' : 'info'}>
						{accessFlow?.triage.text ?? ''}
					</BadgeText>
				</elements.badgeTextContainer>
			);
		}
		return null;
	};

	return (
		<elements.container
			as={isStatic ? 'div' : undefined}
			data-focus={isStatic ? false : undefined}
			data-disabled={disabled ?? undefined}
			onClick={handleOnClick}
			{...rest}
		>
			{icon ? <elements.iconContainer variant={variant}>{icon}</elements.iconContainer> : null}

			<elements.actionTextContainer variant={variant} $hasBadge={hasBadge} data-has-icon={icon != undefined}>
				<Text variant='labelLargeSemiBold'>{title}</Text>
				{subTitle ? (
					<Text
						variant='bodyRegular'
						style={{
							color: tokens.color.content.muted,
						}}
					>
						{subTitle}
					</Text>
				) : null}
				{renderBadge()}
			</elements.actionTextContainer>

			{!hideArrow ? (
				<elements.arrowContainer variant={variant}>
					<Icon type='arrowRight' size='small' />
				</elements.arrowContainer>
			) : null}
		</elements.container>
	);
}
